/* tts-helper.js — wrapper around the browser SpeechSynthesis API, shared
 * by Timbrica language tools that need to speak a word or sentence.
 *
 * Browser speechSynthesis only — no network, no WASM. Voices load
 * asynchronously on some browsers; ready() resolves once they are available.
 *
 * Voice quality: browsers ship a wide range of English voices, from the
 * robotic eSpeak fallback on Linux to the near-human Microsoft "Natural"
 * (Edge) and Google / Apple-enhanced voices. pickVoice() / rankedVoices()
 * score every candidate so tools speak with the best available voice, and
 * degrade gracefully (best → any → browser default) when nothing good
 * exists. Tools may also request a male / female voice via opts.gender.
 *
 * Plain IIFE. Exposes window.TTSHelper.
 */
(function (root, factory) {
  'use strict';
  var T = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = T;
  if (typeof window !== 'undefined') window.TTSHelper = T;
  else if (root) root.TTSHelper = T;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var synth = (typeof window !== 'undefined') ? window.speechSynthesis : null;
  var listeners = [];
  var readyResolve, readyPromise = new Promise(function (res) { readyResolve = res; });
  var settled = false;

  function isSupported() {
    return !!(synth && typeof window !== 'undefined' &&
      typeof window.SpeechSynthesisUtterance === 'function');
  }

  function voices() { return isSupported() ? (synth.getVoices() || []) : []; }

  function settle() {
    if (settled) return;
    if (voices().length) { settled = true; readyResolve(voices()); }
  }

  if (isSupported()) {
    settle();
    try {
      synth.addEventListener('voiceschanged', function () {
        settle();
        // The catalog just changed, so any verdict drawn from the old one is
        // now standing on evidence that no longer exists. (freshenVerdicts is
        // declared below; function declarations hoist, and this runs on an
        // event long after the factory has finished.)
        freshenVerdicts();
        for (var i = 0; i < listeners.length; i++) {
          try { listeners[i](voices()); } catch (e) { /* ignore */ }
        }
      });
    } catch (e) { /* older browsers */ }
    // Some browsers populate voices only after a tick.
    setTimeout(settle, 250);
    setTimeout(function () { if (!settled) { settled = true; readyResolve(voices()); } }, 2000);
  } else {
    settled = true; readyResolve([]);
  }

  /* ── voice gender ──────────────────────────────────────────────────
   * SpeechSynthesisVoice carries no gender field, so we map the common
   * English TTS voice first-names (Microsoft, Google, Apple, Android)
   * to a gender. Unknown voices return null — tools then fall back to
   * "any best voice" rather than guessing wrong. */
  var FEMALE = ('aria jenny michelle ana sara sarah zira hazel susan eva catherine '
    + 'linda heather hortense samantha karen moira tessa fiona victoria allison ava '
    + 'serena kate sonia libby maisie clara amber ashley cora elizabeth emma jane '
    + 'monica nora joanna kendra kimberly salli ivy female woman').split(' ');
  var MALE = ('guy davis tony eric jason christopher brandon david mark george ryan '
    + 'william james daniel alex fred oliver arthur thomas richard liam aaron '
    + 'matthew justin joey kevin male man').split(' ');
  var FSET = {}, MSET = {};
  FEMALE.forEach(function (n) { FSET[n] = 1; });
  MALE.forEach(function (n) { MSET[n] = 1; });

  function voiceGender(v) {
    if (!v) return null;
    var hay = ((v.name || '') + ' ' + (v.voiceURI || '')).toLowerCase();
    var toks = hay.split(/[^a-z]+/);
    for (var i = 0; i < toks.length; i++) {
      if (FSET[toks[i]]) return 'female';
      if (MSET[toks[i]]) return 'male';
    }
    return null;
  }

  /* Quality score — higher is better. Tuned so the natural / cloud voices
   * always outrank the robotic local fallbacks. */
  function scoreVoice(v) {
    var hay = ((v.name || '') + ' ' + (v.voiceURI || '')).toLowerCase();
    var s = 0;
    if (hay.indexOf('natural') >= 0) s += 100;        // MS Edge "… Online (Natural)"
    if (hay.indexOf('google') >= 0) s += 70;          // Chrome desktop Google voices
    if (hay.indexOf('microsoft') >= 0) s += hay.indexOf('online') >= 0 ? 60 : 30;
    if (hay.indexOf('enhanced') >= 0 || hay.indexOf('premium') >= 0) s += 55; // Apple
    if (hay.indexOf('siri') >= 0) s += 45;
    if (hay.indexOf('espeak') >= 0 || hay.indexOf('pico') >= 0) s -= 60;       // robotic
    if (v.localService === false) s += 8;             // cloud voices tend to be richer
    if (v.default) s += 5;
    if ((v.lang || '').toLowerCase().indexOf('en-us') === 0) s += 4;
    else if ((v.lang || '').toLowerCase().indexOf('en-gb') === 0) s += 2;
    return s;
  }

  /* ── learned reliability ────────────────────────────────────────────
   * scoreVoice() above ranks by *quality*, which puts the cloud voices on
   * top ("… Online (Natural)" +100, "Google …" +70, localService:false +8).
   * On a connection that cannot stream them the engine either starts late or
   * delivers a fragment of the sentence — while still reporting success.
   * (Field 2026-08-06: bug #1304 on /dictation-practice — Edge desktop, 327
   * voices, three rage-clicks on Play, "предложение не может прозвучать
   * полностью, всё время заедает"; plus 25 sessions across 6 tools whose
   * utterance never started at all.)
   *
   * Rather than downgrade every visitor to a robotic local voice, remember
   * what actually failed on THIS device and re-rank around it. Session-scoped
   * on purpose: a reload gives the premium voice another chance, so a single
   * bad minute never costs a user the good voice for good. */
  var MEM_KEY = '__ttsHelper.reliability.v1';
  var badVoices = {}, avoidNetwork = false, engineSilent = false;
  /* How many voices existed when `avoidNetwork` / `engineSilent` were decided.
   * A verdict about the engine is only as good as the ladder it was drawn from
   * — see freshenVerdicts(). */
  var memVoices = 0;

  function store() {
    // The UMD header above promises this file also loads outside a browser.
    return (typeof window !== 'undefined' && window.sessionStorage) || null;
  }

  (function loadMem() {
    try {
      var s = store(); if (!s) return;
      var raw = s.getItem(MEM_KEY);
      if (!raw) return;
      var o = JSON.parse(raw) || {};
      badVoices = o.v || {};
      avoidNetwork = !!o.net;
      engineSilent = !!o.silent;
      memVoices = o.n || 0;
    } catch (e) { /* private mode / storage disabled */ }
  })();

  function saveMem() {
    try {
      var s = store(); if (!s) return;
      s.setItem(MEM_KEY, JSON.stringify({ v: badVoices, net: avoidNetwork, silent: engineSilent, n: memVoices }));
    } catch (e) { /* private mode / storage disabled */ }
  }

  /* Record a global verdict together with the evidence it rests on. */
  function latch(which) {
    if (which === 'net') avoidNetwork = true; else engineSilent = true;
    memVoices = voices().length;
    saveMem();
  }

  /* ── a verdict is only as good as the catalog it was drawn from ──────────
   * `avoidNetwork` and `engineSilent` are statements about the whole transport
   * and the whole engine, but they are inferred from whichever voices happened
   * to exist at the moment of the failure — and that set GROWS after load.
   * Edge publishes its ~300-voice online catalog a beat late, and a Windows
   * localised to a non-English UI ships no English local voice at all. So an
   * early press can conclude "this engine says nothing" while holding an EMPTY
   * English ladder, and then keep applying that conclusion to a catalog of 324
   * working voices for the rest of the visit — each press spending one doomed
   * probe and never re-opening the question.
   *
   * (Field 2026-08-15, /english-grammar-course: Edge 150, 324 voices, every
   * press reported attempt 1 / retried 0. The ladder had not run out of voices;
   * it had been shut down by a verdict older than the evidence.)
   *
   * Binding the verdict to its catalog size fixes that structurally: a
   * materially larger catalog is different evidence, so the global verdicts
   * retire themselves. Per-voice `badVoices` survive — those are keyed to a
   * voice identity, which has not changed. */
  function freshenVerdicts() {
    var n = voices().length;
    if (n <= memVoices) return;
    if (engineSilent || avoidNetwork) { engineSilent = false; avoidNetwork = false; }
    memVoices = n;
    saveMem();
  }

  function voiceKey(v) { return v ? (v.voiceURI || v.name || '') : ''; }
  function isNetworkVoice(v) { return !!v && v.localService === false; }

  function noteFailure(v, reason) {
    // A late start is a symptom of a cold or busy engine, not of a broken
    // voice — measured cold start on a fresh browser is p50 708 ms and reaches
    // 1.2 s, and a loaded machine goes further still. Retrying is enough; a
    // local voice must not be condemned for it, or a healthy device slowly
    // blacklists everything it owns.
    // `no_voice` means there was nothing to speak WITH — no voice object was
    // ever attached — so it says nothing about any particular voice either.
    if (reason !== 'no_start' && reason !== 'no_voice') {
      var k = voiceKey(v);
      if (k) badVoices[k] = (badVoices[k] || 0) + 1;
    }
    // A cloud voice rarely fails alone — the transport is the shared cause, so
    // one proven failure retires the whole cloud tier for this visit instead of
    // paying its start latency again on every single press.
    if (isNetworkVoice(v)) latch('net');
    else saveMem();
  }
  function noteSuccess(v) {
    var k = voiceKey(v), dirty = false;
    if (k && badVoices[k]) { delete badVoices[k]; dirty = true; }
    if (engineSilent) { engineSilent = false; dirty = true; }   // it speaks after all
    if (dirty) saveMem();
  }

  /* Voices whose language starts with `lang` (e.g. 'en'). */
  function getVoices(lang) {
    var all = voices();
    if (!lang) return all.slice();
    lang = lang.toLowerCase();
    return all.filter(function (v) {
      return (v.lang || '').toLowerCase().indexOf(lang) === 0;
    });
  }

  /* Best-first ranked list of voices for a language. When an explicit region is
   * requested (e.g. 'en-GB' / 'en-US'), matching-region voices are ranked ahead
   * of other-region ones — so "British" actually speaks British when the device
   * has such a voice — then fall back to other regions (quality orders within
   * each group). No region (plain 'en') → pure quality order, as before. */
  function rankedVoices(lang) {
    var want = (lang || 'en').toLowerCase();
    var list = getVoices(want.slice(0, 2));
    var region = /^[a-z]{2}-[a-z]{2}/.test(want) ? want.slice(0, 5) : '';
    return list.map(function (v) {
        return {
          v: v, s: scoreVoice(v),
          r: !!region && (v.lang || '').toLowerCase().indexOf(region) === 0,
          bad: badVoices[voiceKey(v)] || 0,
          // Two different reasons to send a cloud voice down the list.
          // `avoidNetwork`: this transport has already failed here.
          // `engineSilent`: the next press is a PROBE — "does this engine make
          // any sound at all?" — and a local voice answers it in a fraction of
          // the time (measured on Edge/Windows: local starts p50 186 ms warm,
          // 760 ms cold; an Online (Natural) voice needs 1.6-1.9 s even warm).
          // Probing with the slowest class is what made recovery impossible.
          net: ((avoidNetwork || engineSilent) && isNetworkVoice(v)) ? 1 : 0,
        };
      })
      .sort(function (a, b) {
        if (a.bad !== b.bad) return a.bad - b.bad;         // proven broken here — last
        if (a.net !== b.net) return a.net - b.net;         // retired transport — next-last
        if (region && a.r !== b.r) return a.r ? -1 : 1;    // requested region first
        return b.s - a.s;                                  // then by quality
      })
      .map(function (o) { return o.v; });
  }

  /* Pick a voice for a language.
   * prefs (optional): { gender:'male'|'female', voiceURI, name }
   * Falls back: explicit URI/name → best of requested gender → best overall
   * → browser default (null). */
  function pickVoice(lang, prefs) {
    prefs = prefs || {};
    var ranked = rankedVoices(lang);
    if (!ranked.length) return null;
    if (prefs.voiceURI) {
      for (var i = 0; i < ranked.length; i++)
        if (ranked[i].voiceURI === prefs.voiceURI) return ranked[i];
    }
    if (prefs.name) {
      var nm = String(prefs.name).toLowerCase();
      for (var j = 0; j < ranked.length; j++)
        if ((ranked[j].name || '').toLowerCase() === nm) return ranked[j];
    }
    if (prefs.gender) {
      for (var k = 0; k < ranked.length; k++)
        if (voiceGender(ranked[k]) === prefs.gender) return ranked[k];
      // no gender match — fall through to best overall
    }
    return ranked[0];
  }

  function clamp(v, lo, hi, dflt) {
    v = (typeof v === 'number' && isFinite(v)) ? v : dflt;
    return Math.max(lo, Math.min(hi, v));
  }

  /* A measurable share of visitors has a speechSynthesis that accepts speak()
   * and never produces anything — no 'start', no 'error', no sound (stripped
   * Windows voice packs, broken TTS services; field signature: silent_click +
   * rage_click on listen buttons in every Chromium flavor, bug report
   * 2026-07-29 on /vowel-trainer). A second, quieter failure delivers a
   * fragment of the sentence and then reports success (bug #1304, see the
   * reliability block above). speak() below treats both as the same thing —
   * the utterance was not delivered — and retries down the voice ladder. */
  function ttsTelemetry(payload) {
    try {
      if (!window.__ae) return;
      var slugMeta = document.querySelector('meta[name="tool-slug"]');
      payload.slug = slugMeta ? slugMeta.content : '';
      payload.f = 'tts_stall';
      payload.voices = voices().length;
      window.__ae('feature_use', payload);
    } catch (e) { /* ignore */ }
  }

  /* ── last-resort notice ─────────────────────────────────────────────────
   * speak() promises exactly one terminal callback, but that promise only
   * reaches the user if the CALLER registered one — and 10 of the 21 call
   * sites register none at all (/english-grammar-course among them, which is
   * why its 2026-08-15 reporter saw literally nothing: press, silence, no
   * message, no button change). A shared primitive whose safety depends on
   * every present and future caller remembering a handler will keep producing
   * that report, so the notice belongs here, at the one point they all pass
   * through. Callers that handle failure themselves never reach this line. */
  // The notice itself lives in timbrica-core.js (__speechDelivery.announce):
  // the legacy safety net there ends in the same words, and two copies of the
  // table and the throttle had already begun to drift. Passing the language
  // lets it say which failure it is — a dead engine, or no voice for this
  // language (Windows browsers carry voices for the system languages only).
  function announceFailure(lang) {
    try {
      if (typeof window === 'undefined') return;
      var D = window.__speechDelivery;
      if (D && typeof D.announce === 'function') D.announce(lang);
    } catch (e) { /* ignore */ }
  }

  /* Fastest plausible delivery, characters per second. Measured 2026-08-06 on
   * Windows SAPI via Edge and Chrome (3 voices × 4 lengths × 2 rates): 7.6–16.7
   * c/s at rate 1, 4.8–9.6 c/s at rate 0.5. The constant sits above every
   * observation, and the verdict below only trips under 40% of that floor, so a
   * healthy read — even from a voice quicker than anything measured — cannot be
   * mistaken for a truncated one. */
  function fastestCps(rate) { return 22 * (rate || 1); }

  /* Did the engine actually read the text out, or just claim it did?
   * `boundary` progress is independent of how fast a voice speaks, so it wins
   * whenever the engine emits it; the clock is the fallback for engines that
   * do not. Short strings are never judged — a one-word utterance is mostly
   * fixed engine overhead and the ratios stop meaning anything. */
  function deliveryVerdict(text, rate, elapsedMs, lastChar, boundaries) {
    var len = String(text).length;
    if (len < 8) return 'ok';
    if (boundaries > 0 && lastChar >= 0 && len >= 25) {
      return lastChar >= len * 0.5 ? 'ok' : 'truncated';
    }
    return elapsedMs < (len / fastestCps(rate)) * 1000 * 0.4 ? 'truncated' : 'ok';
  }

  /* How long to wait for `start` before calling the attempt a loss.
   *
   * A flat constant cannot be right for both cases: measured on this codebase
   * 2026-08-06, the FIRST utterance in a fresh browser starts in p50 708 ms and
   * up to 1.2 s (cold engine), while a warm one starts in p50 233 ms — and a
   * cloud voice has a network round trip on top. So the deadline is generous
   * until the device has shown us what it can do, then follows the evidence.
   * Getting this wrong in the tight direction is what makes a healthy engine
   * look broken. */
  var COLD_LOCAL_MS = 4000, COLD_NET_MS = 6000;
  /* Short deadlines, for a warmed-up engine (attempts 2+) and for the
   * engineSilent probe. These MUST stay above the measured start latency of
   * the class they judge, or they convict healthy voices.
   *
   * Measured 2026-08-15, real Edge on Windows, 13-char utterance, en voices:
   *   local (David/Mark/Zira)        cold 396-760 ms   warm 161-198 ms
   *   cloud (Libby/Aria/Guy Natural) cold 1562-1888 ms warm 1601-1668 ms
   *
   * The single 1500 ms constant that used to serve both classes sat BELOW the
   * cloud floor: Microsoft Libby starts at ~1.66 s on a fast wired link, so
   * every retry or probe that landed on her was lost before she could speak —
   * on a healthy engine, over a healthy connection. That is the second half of
   * the 2026-08-15 report (the reporter was on 4G, slower still). Split per
   * class, each with room above its own measurement. */
  var WARM_LOCAL_MS = 1500;       // ~2x the slowest cold local start observed
  var WARM_NET_MS = 4000;         // ~2.1x the slowest cloud start observed
  var warmStartMs = 0;            // slowest start actually observed on this page

  function startDeadline(v, attempt) {
    var net = isNetworkVoice(v);
    // Only the opening attempt pays for a cold engine. By the second one the
    // engine has already had seconds to wake up, so waiting that long again
    // just makes the user stare at a "Stop" button. A cloud voice still has a
    // round trip to pay, warm engine or not — hence the split.
    if (attempt > 1) return net ? WARM_NET_MS : WARM_LOCAL_MS;
    // Once the engine has proven it produces nothing, stop spending seconds on
    // it every press — one short probe per press is enough to notice recovery.
    // rankedVoices() aims that probe at a local voice precisely so this budget
    // can be short; when the device has no local voice for the language (a
    // Windows in a non-English UI has none), the cloud budget applies instead.
    if (engineSilent) return net ? WARM_NET_MS : WARM_LOCAL_MS;
    var cold = net ? COLD_NET_MS : COLD_LOCAL_MS;
    if (!warmStartMs) return cold;
    return Math.max(2000, Math.min(cold, warmStartMs * 3 + 1000));
  }

  var RETRY_GAP_MS = 90;
  var MAX_ATTEMPTS = 3;
  var generation = 0;          // bumped by every speak()/cancel() — see below
  var live = null;             // strong ref: a GC'd utterance dies mid-sentence

  /* Speak text. opts: {lang, rate, pitch, volume, gender, voice|voiceURI|voiceName,
   * onend, onerror, onstart, onstall, stallMs, onboundary}
   *
   * onboundary(e) receives the engine's own word boundaries (e.charIndex into
   * `text`). Many voices never send them — every Chrome "Google" voice and all
   * of Chromium on Android — so a caller must not wait for one.
   *
   * Contract: exactly one terminal callback fires — onend on delivery, else
   * onerror (or onstall when the engine never spoke at all). It is never
   * silent, so a caller that flips a button to "playing" always gets it back.
   * Undelivered utterances are retried down the voice ladder first. */
  function speak(text, opts) {
    opts = opts || {};
    if (!isSupported() || !text) {
      if (opts.onerror) opts.onerror(new Error('tts_unsupported'));
      else if (opts.onstall) opts.onstall(new Error('tts_unsupported'));
      return null;
    }
    var str = String(text);
    var lang = opts.lang || 'en-US';
    var rate = clamp(opts.rate, 0.1, 10, 1);
    var myGen = ++generation;
    // Voices may have arrived since the last press; a verdict drawn from the
    // smaller catalog must not decide this one.
    freshenVerdicts();
    var attempt = 0, tried = {}, settled = false;
    var allNoStart = true;      // did every attempt in this walk die without a start?
    var everHadVoice = false;   // did any attempt actually get a voice object?
    var maxAttempts = engineSilent ? 1 : MAX_ATTEMPTS;

    try { synth.cancel(); } catch (e) { /* ignore */ }

    function superseded() { return myGen !== generation; }

    /* Next candidate. rankedVoices() is re-read every attempt on purpose: a
     * failure has just been recorded, so the ladder has already re-ordered
     * itself away from the transport that let us down. */
    function nextVoice() {
      if (attempt === 0 && opts.voice) return opts.voice;
      var ranked = rankedVoices(lang);
      for (var i = 0; i < ranked.length; i++) {
        if (!tried[voiceKey(ranked[i])]) return ranked[i];
      }
      return null;
    }

    function finish(kind, err, ev) {
      if (settled) return;
      settled = true;
      if (kind === 'end') { if (opts.onend) opts.onend(ev); return; }
      var mute = err && (err.reason === 'no_start' || err.reason === 'no_voice');
      if (mute && opts.onstall) { opts.onstall(err); return; }
      if (opts.onerror) { opts.onerror(err); return; }
      if (opts.onstall) { opts.onstall(err); return; }
      // The caller asked to hear about success only (e.g. minimal-pairs walks a
      // word list on onend). Handing it nothing freezes that walk and leaves its
      // button on "playing" forever, so close the utterance out instead: it IS
      // over, and the failure is already in telemetry. A caller that wants to
      // know the difference has onerror.
      if (opts.onend) { opts.onend({ type: 'end', undelivered: true }); return; }
      // Nobody is listening at all — say it ourselves rather than let the press
      // vanish into silence.
      if (!opts.quietFailure) announceFailure(lang);
    }

    function tryOnce() {
      if (settled || superseded()) return;
      var v = nextVoice();
      // A voice the caller named is the user's own pick (vowel-trainer lets
      // them choose one). Substituting another behind their back would be a
      // silent change of what they asked for — fail instead and let the caller,
      // which knows about its own fallbacks, decide.
      var explicit = (attempt === 0 && !!opts.voice);
      attempt++;
      if (v) { tried[voiceKey(v)] = 1; everHadVoice = true; }

      var u = new window.SpeechSynthesisUtterance(str);
      u.lang = lang;
      u.rate = rate;
      u.pitch = clamp(opts.pitch, 0, 2, 1);
      u.volume = clamp(opts.volume, 0, 1, 1);
      // A bad voice object must not kill the whole utterance — the lang hint
      // still routes to a usable engine voice.
      if (v) { try { u.voice = v; } catch (e) { /* speak with lang hint only */ } }
      // Tells the global legacy safety net in timbrica-core.js to keep its hands
      // off: this utterance already has a fuller ladder behind it, and a second
      // layer of retries on top would speak the same sentence twice.
      u.__tmbManaged = 1;
      live = u;

      var startedAt = 0, bounds = 0, lastChar = -1, guard = 0, closed = false;
      var queuedAt = 0;
      var deadline = opts.stallMs || startDeadline(v, attempt);

      function disarm() { if (guard) { clearTimeout(guard); guard = 0; } }

      /* One attempt failed. Retry down the ladder if there is anywhere left to
       * go, otherwise hand the caller a terminal error — never nothing. */
      function failed(reason) {
        if (closed || settled || superseded()) return;
        closed = true; disarm();
        // Having nothing to speak WITH is not evidence that the engine is mute
        // — it means this language has no voice on this device yet (a Windows
        // in a non-English UI ships none, and Edge's online catalog lands a
        // beat after load). Filing it as a dead engine is what latched the
        // verdict in the 2026-08-15 report, so name it for what it is.
        if (reason === 'no_start' && !v) reason = 'no_voice';
        if (reason !== 'no_start' && reason !== 'no_voice') allNoStart = false;
        noteFailure(v, reason);
        try { synth.cancel(); } catch (e) { /* ignore */ }
        var more = attempt < maxAttempts && !explicit && nextVoice();
        ttsTelemetry({
          reason: reason, attempt: attempt, retried: more ? 1 : 0,
          voice: v ? String(v.name || '').slice(0, 40) : '',
          net: isNetworkVoice(v) ? 1 : 0, len: str.length,
        });
        if (more) { setTimeout(tryOnce, RETRY_GAP_MS); return; }
        // Every voice we could reach stayed mute. Remember that, so the next
        // press probes once and briefly instead of walking the whole ladder
        // again — the very first success clears it. `everHadVoice` is the
        // guard that keeps this a statement about the ENGINE: a walk that
        // never had a voice to try proves nothing about whether it can speak.
        if (allNoStart && everHadVoice && !engineSilent) latch('silent');
        var err = new Error('tts_' + reason);
        err.reason = reason;
        finish('fail', err);
      }

      u.onstart = function (e) {
        if (closed || superseded()) return;
        startedAt = Date.now();
        if (queuedAt) warmStartMs = Math.max(warmStartMs, startedAt - queuedAt);
        disarm();
        if (opts.onstart) opts.onstart(e);
      };
      u.onboundary = function (e) {
        bounds++;
        if (e && typeof e.charIndex === 'number' && e.charIndex > lastChar) lastChar = e.charIndex;
        // Word-by-word highlight for the caller. Only the attempt that is
        // actually speaking reports: a retry restarts the text from 0, and a
        // superseded one must not move a highlight that belongs to the next.
        if (opts.onboundary && !closed && !superseded()) {
          try { opts.onboundary(e); } catch (x) { /* a caller's bug must not stop the speech */ }
        }
      };
      u.onend = function (e) {
        if (closed || settled || superseded()) return;
        disarm();
        // Never started at all: some engines close a swallowed utterance with
        // `end` instead of `error`.
        if (!startedAt) { failed('no_start'); return; }
        if (deliveryVerdict(str, rate, Date.now() - startedAt, lastChar, bounds) === 'truncated') {
          failed('truncated'); return;
        }
        closed = true;
        noteSuccess(v);
        finish('end', null, e);
      };
      u.onerror = function (e) {
        // A cancel() we issued ourselves is not the engine's fault.
        if (e && (e.error === 'canceled' || e.error === 'interrupted') && (settled || superseded())) return;
        failed('error_' + ((e && e.error) || 'unknown'));
      };

      // Chrome occasionally drops an utterance queued in the same tick.
      setTimeout(function () {
        if (closed || settled || superseded()) return;
        queuedAt = Date.now();
        try { synth.speak(u); } catch (e) { failed('threw'); return; }
        guard = setTimeout(function () { if (!startedAt) failed('no_start'); }, deadline);
      }, 0);
    }

    tryOnce();
    return live;
  }
  function cancel() {
    generation++; live = null;
    if (isSupported()) { try { synth.cancel(); } catch (e) { /* ignore */ } }
  }
  /* Returns false where pause() would END the utterance instead (Android: Chromium's
   * Pause() is StopSpeaking(), Resume() is empty and no end event follows — measured
   * 2026-10-09). There the caller must cancel() and re-speak from its own position;
   * window.__speechKeepAlive.pauseIsStop (timbrica-core.js) is the one place that knows. */
  function pause() {
    if (!isSupported()) return false;
    var ka = (typeof window !== 'undefined') ? window.__speechKeepAlive : null;
    if (ka && ka.pauseIsStop) return false;
    try { synth.pause(); return true; } catch (e) { return false; }
  }
  function resume() { if (isSupported()) { try { synth.resume(); } catch (e) { /* ignore */ } } }
  function speaking() { return isSupported() && synth.speaking; }

  function onVoicesChanged(fn) { if (typeof fn === 'function') listeners.push(fn); }

  return {
    isSupported: isSupported,
    ready: function () { return readyPromise; },
    getVoices: getVoices,
    rankedVoices: rankedVoices,
    pickVoice: pickVoice,
    voiceGender: voiceGender,
    scoreVoice: scoreVoice,
    speak: speak,
    cancel: cancel,
    pause: pause,
    resume: resume,
    speaking: speaking,
    onVoicesChanged: onVoicesChanged,
    // Exposed for the delivery-contract guard (tests/playwright/tts-delivery-contract.mjs)
    // and for tools that want to show which voice they ended up on.
    deliveryVerdict: deliveryVerdict,
    reliability: function () { return { bad: badVoices, avoidNetwork: avoidNetwork, engineSilent: engineSilent, voicesAtVerdict: memVoices }; },
    resetReliability: function () { badVoices = {}; avoidNetwork = false; engineSilent = false; warmStartMs = 0; memVoices = 0; saveMem(); }
  };
});
